import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TabList, Tab, mergeClasses, Tooltip } from '@fluentui/react-components';
import { WandFilled } from '@fluentui/react-icons';

import { UnselectedAdPlacementSVGIcon } from '../../media/unselected-ad-placement-svg-icon';
import { SelectedAdPlacementSVGIcon } from '../../media/selected-ad-placement-svg-icon';
import { ViewName } from '../../model/utils';
import { useStyles } from './style';

const AdPlacementId = 'edit-page-ad-placement-view';
const AdjustId = 'edit-page-adjust-view';

export const NavigationView = ({
  i18n,
  currentView,
  setCurrentView,
  isAdjustButtonDisabled,
  adjustButtonTooltip,
  updateAdPlacementImage,
}) => {
  const classes = useStyles();

  const isAdPlacementSelected = useCallback(() => (
    currentView === ViewName.PlacementListView || currentView === ViewName.PlacementEditView
  ), [currentView]);

  const isAdjustSelected = useCallback(() => (
    currentView === ViewName.ImageAdjustView || currentView === ViewName.BackgroundChangeView
  ), [currentView]);

  const selectedItemId = useMemo(() => {
    if (isAdPlacementSelected()) {
      return AdPlacementId;
    } else if (isAdjustSelected()) {
      return AdjustId;
    }
    return null;
  }, [isAdPlacementSelected, isAdjustSelected]);

  const handleAdPlacementClick = useCallback(() => {
    setCurrentView(ViewName.PlacementListView);
    updateAdPlacementImage();
  }, [setCurrentView, updateAdPlacementImage]);

  const handleAdjustClick = useCallback(() => {
    setCurrentView(ViewName.ImageAdjustView);
  }, [setCurrentView]);

  const items = useMemo(() => ([
    {
      id: AdPlacementId,
      name: _TL_('Ad placement'),
      onClick: handleAdPlacementClick,
      icon: <UnselectedAdPlacementSVGIcon />,
      selectedIcon: <SelectedAdPlacementSVGIcon />,
      isSelected: isAdPlacementSelected(),
    },
    {
      id: AdjustId,
      name: _TL_('Adjust'),
      onClick: handleAdjustClick,
      icon: <WandFilled />,
      isSelected: isAdjustSelected(),
      disabled: isAdjustButtonDisabled,
      tooltip: adjustButtonTooltip,
    },
  ]), [adjustButtonTooltip, handleAdPlacementClick, handleAdjustClick, isAdPlacementSelected, isAdjustButtonDisabled, isAdjustSelected]);

  const tabItem = useCallback(item => (
    <Tab
      key={item.id}
      value={item.id}
      icon={selectedItemId === item.id && item.selectedIcon ? item.selectedIcon : item.icon}
      onClick={item.onClick}
      disabled={item.disabled}
      className={mergeClasses(item.id, classes.tabItem)}
    >
      {i18n.getString(item.name)}
    </Tab>
  ), [classes.tabItem, i18n, selectedItemId]);

  return (
    <div className={mergeClasses(classes.editPageNavigation, 'edit-page-navigation')}>
      <TabList vertical selectedValue={selectedItemId} className={classes.tabList}>
        {items.map(item => (
          <div key={item.id}>
            <div className={mergeClasses('tab-item', classes.tabItemWrapper, selectedItemId === item.id && 'selected')}>
              {item.tooltip ?
                <Tooltip
                  withArrow
                  relationship="label"
                  positioning="below"
                  content={item.tooltip}
                >
                  {tabItem(item)}
                </Tooltip> :
                tabItem(item)
              }
            </div>
          </div>
        ))}
      </TabList>
    </div>
  );
};

NavigationView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  currentView: PropTypes.string.isRequired,
  setCurrentView: PropTypes.func.isRequired,
  isAdjustButtonDisabled: PropTypes.bool,
  adjustButtonTooltip: PropTypes.string,
  updateAdPlacementImage: PropTypes.func.isRequired,
};

NavigationView.defaultProps = {
  isAdjustButtonDisabled: false,
  adjustButtonTooltip: undefined,
};
